import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { MonoMeta } from "@/design/primitives";
import { useMediaQuery } from "@/hooks/useMediaQuery";

const BEAT_KIND = "beat";
const RAIL_NEXT = "rail-next";
const RAIL_STATE = "rail-state";
const RAIL_BRAND = "rail-brand";
const SCHEDULE_ROW = "schedule-row";

const BRAND_ORDER = [
  "True Joy Birthing", "TrustOffice", "AeriusView", "WingPoint",
  "TrustMinutes", "SocializeVideo", "StenoDesk", "Unsorted",
];

const BRAND_TITLE = {
  "True Joy Birthing": "True Joy Birthing", "TrustOffice": "TrustOffice",
  "AeriusView": "AeriusView", "WingPoint": "WingPoint",
  "TrustMinutes": "TrustMinutes", "SocializeVideo": "SocializeVideo",
  "StenoDesk": "StenoDesk", "Unsorted": "Not sorted yet",
};

const TONE_DOT = {
  red: "bg-os-red", amber: "bg-os-amber", green: "bg-os-green", gold: "bg-os-gold",
};

function fmtStamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

// ── rail ─────────────────────────────────────────────────────
function RailBtn({ active, onClick, tone, count, title, dashed, mono, children }) {
  return (
    <button
      type="button"
      data-testid="calendar-rail-btn"
      onClick={onClick}
      className={cn(
        "flex w-full items-start gap-2 rounded-md border px-2.5 py-1.5 text-left transition",
        active ? "border-os-border bg-os-wash shadow-[inset_2px_0_0_0_#FFC838]" : "border-transparent hover:bg-os-wash/60",
      )}
    >
      <span className={cn("mt-1.5 h-[7px] w-[7px] shrink-0 rounded-full", tone ? TONE_DOT[tone] : "bg-transparent")} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] text-os-ink">{title}</span>
        {children && <span className="block truncate font-mono text-[10px] text-os-ink-3">{children}</span>}
      </span>
      {count != null && (
        <span className={cn("mt-0.5 shrink-0 rounded border border-os-border px-1.5 py-px font-mono text-[10px] tabular-nums uppercase tracking-[0.06em] text-os-ink-3", mono && "bg-os-surface")}>
          {count}
        </span>
      )}
    </button>
  );
}

function Rail({ items, active, onSelect, brandScope }) {
  const nexts = items.filter((i) => i.kind === RAIL_NEXT);
  const states = items.filter((i) => i.kind === RAIL_STATE);
  const brands = items.filter((i) => i.kind === RAIL_BRAND);
  const isScoped = !!brandScope;

  // When scoped, only show the matching brand rail button
  const visibleBrands = isScoped
    ? brands.filter((b) => b.payload.targetBrand === brandScope.name)
    : brands;

  return (
    <nav data-testid="calendar-rail" className="flex w-full flex-col gap-0.5 lg:w-[15rem]">
      {!isScoped && nexts.length > 0 && (
        <div className="mb-2">
          <p className="mb-1 px-2.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-3">NOW / NEXT</p>
          {nexts.map((n) => (
            <RailBtn
              key={n.slug}
              active={active === n.slug}
              onClick={() => onSelect(n.payload.targetSlug)}
              tone={{ healthy: "green", stale: "amber", broken: "red" }[n.state] || "green"}
              title={n.payload.title}
              mono
            >
              <span className="block truncate font-mono text-[10px] text-os-ink-3">{n.payload.when} · {n.payload.brand}</span>
            </RailBtn>
          ))}
        </div>
      )}
      {!isScoped && (
        <div className="mb-2 flex flex-col gap-0.5">
          <p className="mb-1 px-2.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-3">States</p>
          {states.map((s) => (
            <RailBtn
              key={s.slug}
              active={active === `state:${s.payload.targetState}`}
              onClick={() => onSelect(`state:${s.payload.targetState}`)}
              tone={{ healthy: "green", stale: "amber", broken: "red" }[s.state] || "green"}
              title={s.payload.title}
              count={s.payload.count}
            />
          ))}
        </div>
      )}
      {(!isScoped || visibleBrands.length > 0) && (
        <div className="flex flex-col gap-0.5">
          {!isScoped && (
            <p className="mb-1 px-2.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-3">Brands</p>
          )}
          {isScoped && visibleBrands.length > 0 && (
            <p className="mb-1 px-2.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-3">Brand</p>
          )}
          {visibleBrands.map((b) => (
            <RailBtn
              key={b.slug}
              active={active === `brand:${b.payload.targetBrand}`}
              onClick={() => onSelect(`brand:${b.payload.targetBrand}`)}
              tone={b.payload.tone}
              title={BRAND_TITLE[b.payload.title] || b.payload.title}
              count={b.payload.count}
            />
          ))}
        </div>
      )}
    </nav>
  );
}

// ── schedule list row ────────────────────────────────────────
function ScheduleRow({ row, selected, onSelect }) {
  const p = row.payload || {};
  return (
    <button
      type="button"
      data-testid="calendar-schedule-row"
      onClick={() => onSelect(p.targetSlug)}
      aria-current={selected ? "true" : undefined}
      className={cn(
        "w-full border-t border-os-border px-3 py-2 text-left first:border-t-0",
        "grid grid-cols-[3.5rem_1fr_auto] items-start gap-x-2",
        selected ? "bg-os-wash shadow-[inset_3px_0_0_0_#FFC838]" : "bg-white hover:bg-os-wash/60",
      )}
    >
      <span className="mt-0.5 shrink-0 font-mono text-[11px] tabular-nums text-os-ink-3">
        {p.time}
      </span>
      <span className="min-w-0">
        <span className="flex items-baseline gap-2">
          <span className="truncate text-[13px] font-semibold text-os-ink">{p.title}</span>
          {(p.badge || p.stateWord) && (
            <span className="shrink-0 rounded border border-os-border px-1 py-px font-mono text-[9px] uppercase tracking-[0.06em] text-os-ink-4">
              {p.badge || p.stateWord}
            </span>
          )}
          <span className="shrink-0 rounded border border-os-border px-1 py-px font-mono text-[9px] uppercase tracking-[0.06em] text-os-ink-4">
            {BRAND_TITLE[p.brand] || p.brand}
          </span>
        </span>
        <span className="mt-0.5 flex items-center gap-2 text-[11px] text-os-ink-3">
          <span className={cn("h-[5px] w-[5px] rounded-full", TONE_DOT[p.stateTone])} />
          <span>{p.stateWord}</span>
          <span>· last ran {p.lastRun}</span>
        </span>
      </span>
    </button>
  );
}

// ── reader ──────────────────────────────────────────────────
function Reader({ beat, onClose }) {
  const p = beat?.payload || {};
  if (!beat) {
    return (
      <div data-testid="calendar-reader-empty" className="flex h-full flex-col items-center justify-center px-6 text-center text-os-ink-3">
        <p className="text-[0.875rem]">Pick a beat to see when it runs next.</p>
      </div>
    );
  }
  const isBroken = p.failureStreak > 0;
  return (
    <div data-testid="calendar-reader" className="flex h-full flex-col overflow-y-auto">
      <div className="flex items-start justify-between gap-3 border-b border-os-border px-5 py-4">
        <div className="min-w-0">
          <h2 className="truncate text-[1.125rem] font-semibold leading-tight tracking-[-0.01em] text-os-ink">{p.title}</h2>
          <div className="mt-1 flex items-center gap-2 text-[11px]">
            <span className={cn("h-[7px] w-[7px] rounded-full", TONE_DOT[{ healthy: "green", stale: "amber", broken: "red" }[beat.state] || "green"])} />
            <span className="font-semibold uppercase tracking-[0.06em] text-os-ink-2">
              {{ healthy: "On schedule", stale: "Needs a look", broken: "Stumbled" }[beat.state] || beat.state}
            </span>
            <span className="text-os-ink-4">·</span>
            <span className="rounded border border-os-border px-1 py-px font-mono text-[9px] uppercase tracking-[0.06em] text-os-ink-4">
              {BRAND_TITLE[p.brand] || p.brand}
            </span>
          </div>
        </div>
        {onClose && (
          <button type="button" onClick={onClose} className="shrink-0 rounded-md border border-os-border px-2 py-1 text-[11px] text-os-ink-3 hover:text-os-ink">
            Close
          </button>
        )}
      </div>
      <div className="flex flex-col gap-4 px-5 py-4">
        <div>
          <p className="mb-1 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-3">Runs next</p>
          <p className="text-[0.9375rem] font-semibold text-os-ink">{fmtWhen(p.nextRunAt) || "Not scheduled"}</p>
        </div>
        <div>
          <p className="mb-1 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-3">Last ran</p>
          <p className={cn("text-[0.9375rem] font-semibold", p.lastRunTone === "red" ? "text-os-red" : p.lastRunTone === "amber" ? "text-os-amber" : "text-os-green")}>
            {fmtWhen(p.lastRunAt) || "Has not run yet"}
          </p>
        </div>
        {isBroken && (
          <div className="rounded border border-os-red bg-os-red-tint px-3 py-2">
            <p className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-red">Failure streak · {p.failureStreak}</p>
            {p.lastFailed && <p className="mt-1 text-[0.8125rem] leading-[1.4] text-os-ink">{p.lastFailed}</p>}
          </div>
        )}
        <div>
          <p className="mb-1 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-3">Schedule</p>
          <p className="text-[0.9375rem] leading-[1.4] text-os-ink">{p.scheduleHuman || "—"}</p>
        </div>
        <div>
          <p className="mb-1 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-3">What it does</p>
          <p className="text-[0.875rem] leading-[1.45] text-os-ink">{p.whatHuman || p.what || "—"}</p>
        </div>
        {p.noAgent && (
          <div className="flex items-center gap-1.5">
            <span className="rounded border border-os-border px-1 py-px font-mono text-[9px] uppercase tracking-[0.06em] text-os-ink-4">Script</span>
            <span className="text-[11px] text-os-ink-3">Runs without agent</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── main calendar client ────────────────────────────────────
export function CalendarClient({ items, meta, board, variant = "desktop", onNotice, brandScope }) {
  const desktop = useMediaQuery("(min-width: 1024px)") && variant === "desktop";
  const isScoped = !!brandScope;

  const railItems = useMemo(() => items.filter((i) => i.kind?.startsWith("rail")), [items]);
  const scheduleRows = useMemo(() => items.filter((i) => i.kind === SCHEDULE_ROW), [items]);
  const beats = useMemo(() => items.filter((i) => i.kind === BEAT_KIND), [items]);

  // Scoped default: filter by brand
  const [active, setActive] = useState(isScoped ? `brand:${brandScope.name}` : "today+7");
  const [selectedSlug, setSelectedSlug] = useState(null);

  // Visible schedule rows based on active rail filter
  const visibleRows = useMemo(() => {
    let pool = scheduleRows;
    if (active.startsWith("state:")) {
      const st = active.slice(6);
      pool = pool.filter((r) => r.state === st);
    } else if (active.startsWith("brand:")) {
      const b = active.slice(6);
      pool = pool.filter((r) => r.payload.brand === b);
    } else if (active.startsWith("beat:")) {
      const sl = active.slice(5);
      pool = pool.filter((r) => r.payload.targetSlug === sl);
    } else {
      // default today+7: filter by next 7 days from now
      const now = new Date();
      const days = [];
      for (let i = 0; i <= 7; i++) {
        const d = new Date(now);
        d.setDate(now.getDate() + i);
        days.push(d.toISOString().slice(0, 10));
      }
      pool = pool.filter((r) => days.includes(r.payload.dayKey));
    }
    return pool;
  }, [active, scheduleRows]);

  // Group rows by dayKey
  const groupedRows = useMemo(() => {
    const g = {};
    for (const r of visibleRows) {
      const dk = r.payload.dayKey;
      if (!g[dk]) g[dk] = [];
      g[dk].push(r);
    }
    // Sort each day's rows by time
    for (const dk in g) {
      g[dk].sort((a, b) => a.payload.time.localeCompare(b.payload.time));
    }
    // Sort day keys ascending; label client-side from dayKey (Today/Tomorrow/date)
    const todayStr = new Date().toDateString();
    const tomorrowStr = new Date(Date.now() + 864e5).toDateString();
    const sortedKeys = Object.keys(g).sort();
    return sortedKeys.map((dk) => {
      const d = new Date(`${dk}T12:00:00`);
      const dayName = d.toDateString() === todayStr ? "Today"
        : d.toDateString() === tomorrowStr ? "Tomorrow"
        : d.toLocaleDateString("en-US", { weekday: "long" });
      return {
        dayKey: dk,
        month: d.toLocaleDateString("en-US", { month: "short" }),
        dateNum: d.toLocaleDateString("en-US", { day: "numeric" }),
        dayName,
        rows: g[dk],
        count: g[dk].length,
        brokenCount: g[dk].filter((r) => r.state === "broken").length,
      };
    });
  }, [visibleRows]);

  const selectedBeat = useMemo(
    () => beats.find((b) => b.slug === selectedSlug) || null,
    [beats, selectedSlug],
  );

  const handleRailSelect = (val) => {
    setActive(val);
    setSelectedSlug(null);
  };

  const handleRowSelect = (slug) => {
    setSelectedSlug(slug);
  };

  // Scoped counts
  const scopedRowCount = isScoped
    ? scheduleRows.filter((r) => r.payload.brand === brandScope.name).length
    : scheduleRows.length;
  const scopedBeatCount = isScoped
    ? beats.filter((b) => b.payload?.brand === brandScope.name).length
    : beats.length;

  const List = (
    <div data-testid="calendar-list" className="flex flex-col">
      {groupedRows.length === 0 && (
        <div className="px-4 py-8 text-center text-[0.875rem] text-os-ink-3">
          {isScoped
            ? `No beats scheduled for ${brandScope.name} in this view.`
            : "Nothing scheduled in this view."}
        </div>
      )}
      {groupedRows.map((group) => (
        <div key={group.dayKey} className="flex flex-col">
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-os-border bg-white/95 px-3 py-1.5 backdrop-blur">
            <span className="font-mono text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-os-ink">
              {group.dayName} {group.month} {group.dateNum}
            </span>
            <span className="font-mono text-[0.625rem] text-os-ink-3">
              {group.count} run{group.count !== 1 ? "s" : ""}
              {group.brokenCount > 0 ? (
                <span className="ml-1 text-os-red">· {group.brokenCount} stumbled</span>
              ) : null}
            </span>
          </div>
          {group.rows.map((r) => (
            <ScheduleRow key={r.slug} row={r} selected={selectedSlug === r.payload.targetSlug} onSelect={handleRowSelect} />
          ))}
        </div>
      ))}
    </div>
  );

  const headerTitle = isScoped ? `${brandScope.name} — Calendar` : "Calendar";

  if (!desktop) {
    return (
      <div data-testid="calendar-client" className="flex flex-col gap-3">
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1">
          <h1 className="text-[15px] font-bold tracking-[-0.01em] text-os-ink">{headerTitle}</h1>
          {meta && (
            <MonoMeta className="px-1 text-[0.6875rem] text-os-ink-3">
              {meta.calendars != null
                ? `${meta.event_count} events · ${meta.calendars} calendars · Next 3 days`
                : `${meta.record_count} beats · ${meta.error_count} stumbled · ${meta.stale_count} need a look`} · Current as of {meta.as_of}
            </MonoMeta>
          )}
        </div>
        <Rail items={railItems} active={active} onSelect={handleRailSelect} brandScope={brandScope} />
        {List}
        {selectedBeat && (
          <div className="rounded-card border border-os-border bg-white">
            <Reader beat={selectedBeat} onClose={() => setSelectedSlug(null)} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="calendar-client" className="flex h-[calc(100vh-7rem)] flex-col gap-3">
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        <h1 className="text-[15px] font-bold tracking-[-0.01em] text-os-ink">{headerTitle}</h1>
        {meta && (
          <MonoMeta className="whitespace-nowrap">
            {meta.calendars != null
              ? `${meta.event_count} events · ${meta.calendars} calendars · Next 3 days`
              : isScoped ? `${scopedBeatCount} beats · ${scopedRowCount} runs` : `${meta.record_count} beats · ${meta.error_count} stumbled · ${meta.stale_count} need a look`} · Current as of {meta.as_of}
          </MonoMeta>
        )}
      </div>
      <div className="flex flex-1 gap-3 overflow-hidden">
        <div className="shrink-0 overflow-y-auto border-r border-os-border pr-2">
          <Rail items={railItems} active={active} onSelect={handleRailSelect} brandScope={brandScope} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden border-r border-os-border">
          <div className="flex-1 overflow-y-auto">{List}</div>
        </div>
        <div className="w-[22rem] shrink-0 overflow-y-auto bg-white">
          <Reader beat={selectedBeat} onClose={() => setSelectedSlug(null)} />
        </div>
      </div>
    </div>
  );
}
